'use client';

import { reportError } from '@/utils/error-reporting';
import { Button, FieldError, TextInput, toast, useField, useFormFields, useLocale, useModal } from '@payloadcms/ui';
import { FormState, UIFieldClientComponent } from 'payload';
import { unflatten } from 'payload/shared';
import React, { useState } from 'react';

import { createDocumentInCollectionFromClient } from '../../services/create-collection-document-from-client';
import { Modal } from '../Modal/Modal';

import './styles.scss';

const INLINE_MODULE_INLINE_CONTENT_PATH_KEY = '.inlineContent.'; // modules.{position}.inlineContent.{field_name}
const TENANT_KEY = 'tenant';

const INLINE_MODULE_TO_COLLECTION_MODAL_SLUG_PREFIX = 'inline-module-to-collection-modal';

const InlineModuleToCollectionButton: UIFieldClientComponent = ({ path }) => {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [documentName, setDocumentName] = useState<string>('');
  const [displayNameError, setDisplayNameError] = useState<boolean>(false);

  const locale = useLocale();
  const { openModal, closeModal } = useModal();

  const [BASE_KEY_PATH] = path.split(INLINE_MODULE_INLINE_CONTENT_PATH_KEY); // modules.0
  const INLINE_MODULE_TO_COLLECTION_MODAL_SLUG = `${INLINE_MODULE_TO_COLLECTION_MODAL_SLUG_PREFIX}-${BASE_KEY_PATH.replace(/\./g, '-')}`;
  const BLOCK_TYPE_KEY = `${BASE_KEY_PATH}.blockType`; // module.{position}.blockType
  const SOURCE_TYPE_KEY = `${BASE_KEY_PATH}.sourceType`; // module.{position}.sourceType
  const REFERENCE_KEY = `${BASE_KEY_PATH}.reference`; // module.{position}.sourceType

  const fields = useFormFields(([fields]) => {
    const matchingFields: FormState = {};

    Object.entries(fields).forEach(([key, value]) => {
      if (key.includes(BASE_KEY_PATH) || key === TENANT_KEY) {
        matchingFields[key] = value;
      }
    });

    return matchingFields;
  });

  const { setValue: setSourceTypeValue } = useField({
    path: SOURCE_TYPE_KEY,
  });
  const { setValue: setReferenceTypeValue } = useField({
    path: REFERENCE_KEY,
  });

  const COLLECTION_SLUG = fields[BLOCK_TYPE_KEY]?.value as string | undefined;
  const TENANT_ID = fields[TENANT_KEY]?.value as string | undefined;

  if (!COLLECTION_SLUG) return null;

  const handleAfterCreateInlineToReferenceSwitch = (id: string) => {
    setReferenceTypeValue(id);
    setSourceTypeValue('reference');
  };

  const handleDocumentCreationClick = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isCreating) return;

    if (!documentName) {
      setDisplayNameError(true);
      return;
    }

    setDisplayNameError(false);
    setIsCreating(true);

    const INLINE_VALUES_BY_FIELD_PATH: Record<string, unknown> = Object.assign(
      {},
      ...Object.entries(fields)
        .map(([key, value]) => {
          if (!value.value) return;

          const inlineFieldPath = key.split(INLINE_MODULE_INLINE_CONTENT_PATH_KEY)[1];

          if (!inlineFieldPath) return;

          return {
            [inlineFieldPath as string]: value.value,
          };
        })
        .filter(Boolean),
    );
    const UNFLATTENED_INLINE_VALUES: Record<string, unknown> = unflatten(INLINE_VALUES_BY_FIELD_PATH, {
      object: false,
      delimiter: '.',
      overwrite: true,
      recursive: true,
    });

    try {
      const response = await createDocumentInCollectionFromClient(
        COLLECTION_SLUG,
        {
          ...(TENANT_ID && { tenant: TENANT_ID }),
          ...UNFLATTENED_INLINE_VALUES,
          name: documentName,
          _status: 'published',
        },
        locale.code,
      );

      if (!response.ok) {
        const { errors } = (await response.json()) as { errors: { message: string }[] };
        const errorMessage = errors.map((error) => error.message).join(', ');
        toast.error(errorMessage);

        return;
      }

      const data = (await response.json()) as { doc: { id: string } };
      const { id } = data.doc;
      toast.success(`Document with id ${id} created successfully in ${COLLECTION_SLUG} collection`);
      handleAfterCreateInlineToReferenceSwitch(id);
    } catch (error) {
      reportError(error, {
        flow: 'inline-to-collection',
        collection_slug: COLLECTION_SLUG,
        tenant_id: TENANT_ID,
        locale: locale.code,
        document_name: documentName,
      });
      toast.error('Failed to create document');
    } finally {
      setIsCreating(false);
      closeModal(INLINE_MODULE_TO_COLLECTION_MODAL_SLUG);
    }
  };

  return (
    <div>
      <Modal
        slug={INLINE_MODULE_TO_COLLECTION_MODAL_SLUG}
        aria-label="Convert to collection document"
        className="fv-inline-to-collection-modal"
      >
        <div className="fv-inline-to-collection-modal__content">
          <h1>Create as {COLLECTION_SLUG} collection document</h1>
          <span>Enter the name of the document you want to create, this name is only used to identify the document.</span>
          <form onSubmit={handleDocumentCreationClick}>
            <TextInput
              path="document-name"
              label="Document Name"
              value={documentName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocumentName(e.target.value)}
              required
              showError={displayNameError}
              Error={<FieldError path="document-name" message="Document name is required" showError={displayNameError} />}
            />
            <Button type="submit" buttonStyle="primary" icon="plus" disabled={isCreating}>
              {isCreating ? 'Creating...' : `Create as ${COLLECTION_SLUG} collection document`}
            </Button>
          </form>
        </div>
      </Modal>
      <Button onClick={() => openModal(INLINE_MODULE_TO_COLLECTION_MODAL_SLUG)} buttonStyle="secondary" icon="plus" disabled={isCreating}>
        {`Convert to shared document`}
      </Button>
    </div>
  );
};

export default InlineModuleToCollectionButton;
